import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { auth, FilterItem, firestore, getQuery, OrderItem } from 'services';
import toast from 'react-hot-toast';
import { MONTHS } from 'app-constants';
import { Button, WList } from 'shared/Components';
import FilterControle from 'shared/Components/FlexebleTable/Components/FilterControle';
import { DashboardRoutePath } from 'modules/dashboard/navigation';
import { useModalProvider } from 'context/ModalProviderContext';

import { Box, FlexContainer, Row } from '../../../../shared/Typhography';

import ApproveRequestModal from './modals/ApproveRequest';
import RejectRequestModal from './modals/RejectRequest';
import CreateRequestModal from './modals/CreateRequest';

const Container = styled.div`
  margin-top: 150px;
  padding-bottom: 100px;
`;

export interface PayoutRequest extends DocumentData {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: 'requested' | 'pending' | 'approved' | 'rejected' | 'completed';
  requestedAmount: number;
  paymentMethod?: string;
  approvedAmount?: number;
  artist: {
    id: string;
    profileImageUrl: string;
    name: string;
  };
  requestedBy: {
    id: string;
    profileImageUrl: string;
    name: string;
  };
}

interface RevenueRowProps {
  data: QueryDocumentSnapshot<PayoutRequest>;
  props?: {
    updateStatus: (status: string, id: string) => Promise<void>;
  };
}

const OrderingCarrot = styled.span<{ direction: 'asc' | 'desc' }>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

const dateParse = (date?: Date) => {
  if (!date) return 'Not Set';

  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const StatusBadge = styled.span`
  color: white;
  padding: 5px;
  border-radius: 4px;
  width: max-content;

  &.requested {
    background: orange;
  }
  &.pending {
    background: #d5d55f;
  }
  &.approved {
    background: #30af30;
  }
  &.rejected {
    background: red;
  }
  &.completed {
    background: #30af30;
  }
`;

const PayoutRow = ({ data, props }: RevenueRowProps) => {
  const navigate = useHistory();

  const ACTIONS = {
    details: () => navigate.push(`${DashboardRoutePath.PAYOUT_REQUESTS}/${data.id}`),
    'to-pending': () => props?.updateStatus('pending', data.id),
    'to-requested': () => props?.updateStatus('requested', data.id),
    'to-approved': () => props?.updateStatus('approved', data.id),
    'to-rejected': () => props?.updateStatus('rejected', data.id),
  };

  return (
    <Row style={{ height: '64px' }}>
      <span style={{ flex: 1 }}>
        <StatusBadge className={data.data().status}>{data.data().status}</StatusBadge>
      </span>
      <span style={{ flex: 1 }}>{dateParse(data.data().createdAt.toDate())}</span>
      <span style={{ flex: 1 }}>{dateParse(data.data().updatedAt.toDate())}</span>
      <span style={{ flex: 1 }}>{data.data().artist.name}</span>
      <span style={{ flex: 1 }}>{data.data().requestedBy.name}</span>
      <span style={{ flex: 1 }}>$ {data.data().requestedAmount.toFixed(2)}</span>
      <span style={{ flex: 1 }}>
        {data.data().approvedAmount ? `$ ${data.data().approvedAmount?.toFixed(2)}` : '-'}
      </span>
      <span style={{ flex: 1 }}>{data.data()?.paymentMethod ?? '-'}</span>
      <span style={{ flex: 1 }}>
        <select
          value="label"
          onChange={(event) => ACTIONS[event.target.value as keyof typeof ACTIONS]()}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontSize: '16px',
            outline: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          <option disabled={true} value="label">
            Actions
          </option>
          <option value="details">Details</option>
          {props?.updateStatus && (
            <>
              {data.data().status === 'requested' && <option value="to-pending">Pending</option>}
              {data.data().status === 'pending' && (
                <>
                  <option value="to-requested">Back to requested</option>
                  <option value="to-approved">Approve</option>
                  <option value="to-rejected">Reject</option>
                </>
              )}
            </>
          )}
        </select>
      </span>
    </Row>
  );
};

export default function PayoutRequestsScreen() {
  const user = auth.currentUser;
  const { openModal, closeModal } = useModalProvider();

  const [data, setData] = React.useState<QueryDocumentSnapshot<PayoutRequest>[]>([]);
  const [allFetched, setAllFetched] = React.useState<boolean>();

  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);

  const [users, setUsers] = React.useState<{ label: string; value: string }[] | undefined>();
  const [usersArtists, setUsersArtist] = React.useState<
    { label: string; value: string }[] | undefined
  >();
  const [userDoc, setUserDoc] = React.useState<{
    id: string;
    name: string;
    profileImage: string;
  }>();

  const fetchUsers = async () => {
    if (users) return;

    const userQuery = getQuery('usersWeb', [], []);
    const userDocs = await getDocs(userQuery);

    setUsers(
      userDocs.docs.map((item) => ({
        label: item?.data()?.name ?? item?.data()?.Name ?? '-',
        value: item.id,
      })),
    );

    setUsersArtist(
      userDocs.docs
        .filter((item) => item.data().Role === 'artist')
        .map((item) => ({
          label: `${item?.data()?.name ?? item?.data()?.Name ?? '-'} ${
            item?.data()?.username ? `(${item.data().username})` : ''
          }`,
          value: item.id,
        })),
    );

    const currentUser = userDocs.docs.find((item) => item.id === user?.uid);
    if (currentUser)
      setUserDoc({
        id: currentUser.id,
        name: currentUser?.data()?.Name ?? currentUser?.data()?.name ?? '-',
        profileImage: currentUser.data()?.profileImageUrl ?? '-',
      });
  };

  const load = async (reset = false) => {
    if (!reset && allFetched) return;
    else setAllFetched(false);

    setLoading(true);
    try {
      const nextQuery = getQuery(
        'payoutRequests',
        filters.firebase,
        ordering,
        data.length > 0 && !reset ? data.pop() : undefined,
        20,
      );
      const nextData = await getDocs(nextQuery);
      if (nextData.size != 0)
        setData(
          reset
            ? (nextData.docs as QueryDocumentSnapshot<PayoutRequest>[])
            : [...data, ...(nextData.docs as QueryDocumentSnapshot<PayoutRequest>[])],
        );
      else setAllFetched(true);
    } catch (err) {
      toast.error('Failed to fetch');
      console.log(err);
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc') setOrdering([{ field: attribute, type: 'desc' }]);
      else setOrdering([]);

      return;
    }

    setOrdering([{ field: attribute, type: 'asc' }]);
  };

  const updateStatus = async (status: PayoutRequest['status'], id: string) => {
    const docRef = doc(firestore, `payoutRequests/${id}`);

    if (['pending', 'requested', 'completed'].includes(status)) {
      await updateDoc(docRef, {
        status: status,
        updatedAt: Timestamp.now(),
      });
      const freshData = await getDoc(docRef);
      setData(
        data.map((item) =>
          item.id === id ? (freshData as QueryDocumentSnapshot<PayoutRequest>) : item,
        ),
      );

      const historyRef = collection(firestore, `payoutRequests/${id}/history`);
      await setDoc(doc(historyRef), {
        actor: {
          id: userDoc?.id,
          name: userDoc?.name,
          profileImage: userDoc?.profileImage,
        },
        createdAt: Timestamp.now(),
        updatedAttributes: ['status'],
        updates: [
          {
            attribute: 'status',
            previousValue: data.find((item) => item.id === id)?.data().status,
            value: status,
          },
        ],
      });
      return;
    }

    if (status === 'approved') {
      openModal('approve-request-modal', ApproveRequestModal as React.FC, {
        payoutRequest: data.find((item) => item.id === id),
        close: (refreshDoc = true) => {
          if (refreshDoc)
            getDoc(docRef).then((res) => {
              setData(
                data.map((item) =>
                  item.id === id ? (res as QueryDocumentSnapshot<PayoutRequest>) : item,
                ),
              );
            });

          closeModal('approve-request-modal');
        },
        user: userDoc,
      });
      return;
    }

    if (status === 'rejected') {
      openModal('reject-request-modal', RejectRequestModal as React.FC, {
        payoutRequest: data.find((item) => item.id === id),
        close: (refreshDoc = true) => {
          if (refreshDoc)
            getDoc(docRef).then((res) => {
              setData(
                data.map((item) =>
                  item.id === id ? (res as QueryDocumentSnapshot<PayoutRequest>) : item,
                ),
              );
            });

          closeModal('reject-request-modal');
        },
        user: userDoc,
      });
      return;
    }
  };

  React.useEffect(() => {
    fetchUsers();
    load(true);
  }, [filters.firebase, ordering]);

  return (
    <Container>
      <Box padding="16px">
        <FlexContainer
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <div style={{ flex: 1 }}>
            <FilterControle
              onChange={(item) => {
                setFilters({
                  firebase: [
                    ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                    { attribute: item?.name, value: item?.value, operator: '==' },
                  ],
                  fields: {
                    ...filters.fields,
                    [item.name]: item,
                  },
                });
              }}
              clear={(name: string) => {
                setFilters({
                  firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                  fields: {
                    ...filters.fields,
                    [name]: undefined,
                  },
                });
              }}
              values={filters.fields}
              keys={[
                {
                  name: 'artist.id',
                  placeholder: 'No Artist',
                  options: usersArtists ?? [],
                  disabled: !usersArtists,
                },
                {
                  name: 'status',
                  placeholder: 'No Status',
                  options: [
                    { label: 'Requested', value: 'requested' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Approved', value: 'approved' },
                    { label: 'Rejected', value: 'rejected' },
                    { label: 'Completed', value: 'completed' },
                  ],
                },
                {
                  name: 'requestedBy.id',
                  placeholder: 'No Requestor',
                  options: users ?? [],
                  disabled: !users,
                },
                {
                  name: 'createdAt',
                  placeholder: 'Date Range',
                  options: [],
                  type: 'date-range',
                  onChange: (dates: [Date | null, Date | null]) => {
                    const start = dates['0'];
                    const end = dates['1'];
                    if (start) {
                      start.setUTCHours(0);
                      start.setUTCMinutes(0);
                      start.setUTCSeconds(0);
                    }

                    if (end) {
                      end.setUTCHours(23);
                      end.setUTCMinutes(59);
                      end.setUTCSeconds(59);
                    }

                    if (!start || !end) {
                      if (filters.firebase.find((fil) => fil.attribute === 'createdAt')) {
                        setFilters({
                          firebase: filters.firebase.filter((fil) => fil.attribute !== 'createdAt'),
                          fields: {
                            ...filters.fields,
                            date: undefined,
                          },
                        });
                      }
                      return;
                    }

                    let firebaseFilter: FilterItem[] = filters.firebase;
                    if (start && end) {
                      firebaseFilter.push({
                        value: Timestamp.fromDate(start),
                        attribute: 'createdAt',
                        operator: '>=',
                      });

                      firebaseFilter.push({
                        value: Timestamp.fromDate(end),
                        attribute: 'createdAt',
                        operator: '<=',
                      });
                    } else {
                      firebaseFilter = firebaseFilter.filter(
                        (item) => item?.attribute !== 'createdAt',
                      );
                    }

                    setFilters({
                      firebase: [...firebaseFilter],
                      fields: {
                        ...filters.fields,
                        date: { startDate: start, endDate: end },
                      },
                    });
                  },
                },
              ]}
            />
          </div>

          <div style={{ minWidth: '200px' }}>
            <Button
              margin="10px auto"
              onClick={() =>
                openModal('create-payout-request', CreateRequestModal as React.FC, {
                  user: userDoc,
                  artists: usersArtists,
                  close: async (fetch = true) => {
                    if (fetch) {
                      await load(true);
                    }
                    closeModal('create-payout-request');
                  },
                })
              }
              disabled={!usersArtists || !userDoc}
              width={200}
              height={32}
            >
              Create Payout Request
            </Button>
          </div>
        </FlexContainer>
      </Box>
      <Box padding="16px">
        <Row style={{ height: '64px' }}>
          {/* TODO Convert to component in future implementation */}
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('status')}
          >
            Status
            {ordering?.find((oitem) => oitem?.field === 'status') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'status')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('createdAt')}
          >
            Requested At
            {ordering?.find((oitem) => oitem?.field === 'createdAt') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'createdAt')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('updatedAt')}
          >
            Updated At
            {ordering?.find((oitem) => oitem?.field === 'updatedAt') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'updatedAt')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('artist.name')}
          >
            Artist
            {ordering?.find((oitem) => oitem?.field === 'artist.name') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'artist.name')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('requestedBy.name')}
          >
            Requested By
            {ordering?.find((oitem) => oitem?.field === 'requestedBy.name') && (
              <OrderingCarrot
                direction={
                  ordering?.find((oitem) => oitem?.field === 'requestedBy.name')?.type ?? 'asc'
                }
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('requestedAmount')}
          >
            Requested Amount
            {ordering?.find((oitem) => oitem?.field === 'requestedAmount') && (
              <OrderingCarrot
                direction={
                  ordering?.find((oitem) => oitem?.field === 'requestedAmount')?.type ?? 'asc'
                }
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('approvedAmount')}
          >
            Approved Amount
            {ordering?.find((oitem) => oitem?.field === 'approvedAmount') && (
              <OrderingCarrot
                direction={
                  ordering?.find((oitem) => oitem?.field === 'approvedAmount')?.type ?? 'asc'
                }
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('paymentMethod')}
          >
            Payout Method
            {ordering?.find((oitem) => oitem?.field === 'paymentMethod') && (
              <OrderingCarrot
                direction={
                  ordering?.find((oitem) => oitem?.field === 'paymentMethod')?.type ?? 'asc'
                }
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}>Details</span>
        </Row>
        <WList
          data={data}
          maxHeight={window.innerHeight - 300}
          itemHeight={64}
          itemComponent={PayoutRow}
          props={{
            updateStatus,
          }}
          scrollConfig={{
            fromBottom: 100,
            event: async () => {
              if (!isLoading) load();
            },
          }}
        />
      </Box>
    </Container>
  );
}
